// Lays out a workflow for the canvas from the cards' real sizes, so nothing overlaps. It reads left
// to right, like n8n.
//
// A small layered layout: each step sits in a column to the right of every step that leads to it
// (loops are ignored), columns are ordered top to bottom to cut crossings (a decision's Yes branch
// above its No branch), and an edge that skips columns gets a lane of its own through them, so it
// never runs under a card.
import type { Workflow } from './agentBuilderApi';

export interface Size {
  width: number;
  height: number;
}
export interface Point {
  x: number;
  y: number;
}
export interface Placement {
  positions: Record<string, Point>; // node id -> top-left corner
  waypoints: Record<string, Point[]>; // edge id -> lane points through the columns it skips
  loops: string[]; // edge ids that go back left: drawn as plain step edges
}

const COLUMN_GAP = 90;
const CARD_GAP = 36;
const LANE_GAP = 14;
const FALLBACK: Size = { width: 230, height: 80 };
// Where a decision's Yes and No handles sit on its right edge (fraction of the card's height).
export const YES_AT = 0.3;
export const NO_AT = 0.7;

interface Link {
  from: string;
  to: string;
  shift: number; // how far below the source's centre the link leaves, in px
  bias: number; // ordering nudge: Yes above, No below
}

export function layoutFlow(
  wf: Workflow,
  sizes: Record<string, Size>
): Placement {
  const ids = wf.nodes.map((n) => n.id);
  if (!ids.length) return { positions: {}, waypoints: {}, loops: [] };
  const known = new Set(ids);
  const edges = wf.edges.filter(
    (e) => known.has(e.source) && known.has(e.target)
  );
  const handleOrder = (h?: string | null) =>
    h === 'yes' ? 0 : h === 'no' ? 2 : 1;
  const out: Record<string, typeof edges> = Object.fromEntries(
    ids.map((id) => [id, []])
  );
  for (const e of edges) out[e.source].push(e);
  for (const id of ids)
    out[id].sort(
      (a, b) => handleOrder(a.sourceHandle) - handleOrder(b.sourceHandle)
    );

  // Depth-first from Start (Yes before No): visiting order, and the edges that loop back.
  const state: Record<string, 1 | 2> = {};
  const pre: Record<string, number> = {};
  const post: string[] = [];
  const loops = new Set<string>();
  const visit = (id: string) => {
    state[id] = 1;
    pre[id] = Object.keys(pre).length;
    for (const e of out[id]) {
      if (state[e.target] === 1) loops.add(e.id);
      else if (!state[e.target]) visit(e.target);
    }
    state[id] = 2;
    post.push(id);
  };
  const start = wf.nodes.find((n) => n.type === 'start')?.id ?? ids[0];
  if (start) visit(start);
  for (const id of ids) if (!state[id]) visit(id);
  const forward = edges.filter((e) => !loops.has(e.id));

  // Column ("row" below: one layer of the layout): the longest path from Start.
  const row: Record<string, number> = Object.fromEntries(
    ids.map((id) => [id, 0])
  );
  for (const id of [...post].reverse())
    for (const e of out[id])
      if (!loops.has(e.id))
        row[e.target] = Math.max(row[e.target], row[id] + 1);

  // Lanes: an edge that skips columns gets a placeholder in each column it crosses.
  const size: Record<string, Size> = {};
  for (const id of ids) size[id] = sizes[id] ?? FALLBACK;
  const order: Record<string, number> = { ...pre };
  const links: Link[] = [];
  const lanes: Record<string, string[]> = {};
  for (const e of forward) {
    const h = size[e.source].height;
    const shift =
      e.sourceHandle === 'yes'
        ? (YES_AT - 0.5) * h
        : e.sourceHandle === 'no'
        ? (NO_AT - 0.5) * h
        : 0;
    const bias =
      e.sourceHandle === 'yes' ? -0.3 : e.sourceHandle === 'no' ? 0.3 : 0;
    let prev = e.source;
    lanes[e.id] = [];
    for (let r = row[e.source] + 1; r < row[e.target]; r++) {
      const lane = `\u0000${e.id}\u0000${r}`;
      size[lane] = { width: 0, height: 0 };
      row[lane] = r;
      order[lane] = pre[e.source] + 0.5;
      lanes[e.id].push(lane);
      links.push({
        from: prev,
        to: lane,
        shift: prev === e.source ? shift : 0,
        bias: prev === e.source ? bias : 0,
      });
      prev = lane;
    }
    links.push({
      from: prev,
      to: e.target,
      shift: prev === e.source ? shift : 0,
      bias: prev === e.source ? bias : 0,
    });
  }
  const isLane = (id: string) => id.startsWith('\u0000');

  const rows: string[][] = [];
  for (const id of Object.keys(row)) (rows[row[id]] ??= []).push(id);
  for (const r of rows) r.sort((a, b) => order[a] - order[b]);
  const preds: Record<string, Link[]> = {};
  const succs: Record<string, Link[]> = {};
  for (const l of links) {
    (preds[l.to] ??= []).push(l);
    (succs[l.from] ??= []).push(l);
  }

  // Order each column by the average place of its neighbours (a few sweeps each way).
  const index: Record<string, number> = {};
  const reindex = (r: string[]) => r.forEach((id, i) => (index[id] = i));
  rows.forEach(reindex);
  const sortBy = (r: string[], key: (id: string) => number | null) => {
    const keys = Object.fromEntries(r.map((id) => [id, key(id) ?? index[id]]));
    r.sort((a, b) => keys[a] - keys[b]);
    reindex(r);
  };
  const mean = (xs: number[]) =>
    xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
  for (let pass = 0; pass < 4; pass++) {
    for (let r = rows.length - 2; r >= 0; r--)
      sortBy(rows[r], (id) => mean((succs[id] ?? []).map((l) => index[l.to])));
    for (let r = 1; r < rows.length; r++)
      sortBy(rows[r], (id) =>
        mean((preds[id] ?? []).map((l) => index[l.from] + l.bias))
      );
  }

  // Up and down: each card level with its parents (a Yes child level with the Yes handle, a No child
  // with No), pushed down only as far as needed, then the column is centred on where its cards
  // wanted to be.
  const centre: Record<string, number> = {};
  for (const r of rows) {
    const wanted = r.map((id) =>
      mean((preds[id] ?? []).map((l) => centre[l.from] + l.shift))
    );
    let right = -Infinity;
    let prevLane = true;
    r.forEach((id, i) => {
      const half = size[id].height / 2;
      const gap = isLane(id) || prevLane ? LANE_GAP : CARD_GAP;
      const least = right + gap + half; // -Infinity for the row's first card
      centre[id] = Math.max(
        wanted[i] ?? (least === -Infinity ? 0 : least),
        least
      );
      right = centre[id] + half;
      prevLane = isLane(id);
    });
    const drift = mean(
      r.flatMap((id, i) => {
        const want = wanted[i];
        return want === null ? [] : [want - centre[id]];
      })
    );
    if (drift) for (const id of r) centre[id] += drift;
  }

  // Across: each column as wide as its widest card.
  const left: number[] = [];
  const width: number[] = rows.map((r) =>
    Math.max(0, ...r.map((id) => size[id].width))
  );
  rows.forEach(
    (_, r) => (left[r] = r ? left[r - 1] + width[r - 1] + COLUMN_GAP : 0)
  );

  const top = Math.min(...ids.map((id) => centre[id] - size[id].height / 2));
  const positions: Record<string, Point> = {};
  for (const id of ids)
    positions[id] = {
      x: left[row[id]],
      y: centre[id] - size[id].height / 2 - top,
    };
  const waypoints: Record<string, Point[]> = {};
  for (const [edge, path] of Object.entries(lanes))
    waypoints[edge] = path.flatMap((lane) => [
      { x: left[row[lane]], y: centre[lane] - top },
      { x: left[row[lane]] + width[row[lane]], y: centre[lane] - top },
    ]);
  return { positions, waypoints, loops: [...loops] };
}
